import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import CallData from "../classes/CallData";
import EditProfileView from "./EditProfileView";
import Loader from "./Loader";
import Popup from "./Popup";
import TwilioClient from "../classes/TwilioClient";
import User from "../models/User";
import TextView from "./TextView";
import CallInteraction from "../classes/CallInteraction";
import CallingProgressView from "./CallingProgressView";
import TextWithToggle from "./TextWithToggle";
import { BuildContext } from "../classes/BuildContext";

type _PageHeaderViewOnConnect = () => void;

type _PageHeaderViewOnMaximizeCall = () => void;

export interface PageHeaderViewProps extends BaseUIProps {
  key?: string;
  title?: string;
  data: CallData;
  callInteraction: CallInteraction;
  enableCallView: boolean;
  client: TwilioClient;
  user: User;
  onConnect: _PageHeaderViewOnConnect;
  onMaximizeCall: _PageHeaderViewOnMaximizeCall;
}

class _PageHeaderViewState extends ObservableComponent<PageHeaderViewProps> {
  static defaultProps = {
    title: "",
    data: null,
    callInteraction: null,
    enableCallView: false,
    client: null,
    user: null,
    onConnect: null,
    onMaximizeCall: null,
  };
  editProfilePopupPopup: Popup;
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: PageHeaderViewProps) {
    super(props);

    this.initState();
  }
  public get title(): string {
    return this.props.title;
  }
  public get data(): CallData {
    return this.props.data;
  }
  public get callInteraction(): CallInteraction {
    return this.props.callInteraction;
  }
  public get enableCallView(): boolean {
    return this.props.enableCallView;
  }
  public get client(): TwilioClient {
    return this.props.client;
  }
  public get user(): User {
    return this.props.user;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("data", this.props.data);

    this.updateSyncProperty("callInteraction", this.props.callInteraction);

    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "callInteraction",
        "client",
        "data",
        "data.connecting",
        "data.deviceReady",
        "enableCallView",
        "title",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: PageHeaderViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.title !== this.props.title) {
      this.fire("title", this);
    }

    if (prevProps.data !== this.props.data) {
      this.updateObservable("data", prevProps.data, this.props.data);

      this.fire("data", this);
    }

    if (prevProps.callInteraction !== this.props.callInteraction) {
      this.updateObservable(
        "callInteraction",
        prevProps.callInteraction,
        this.props.callInteraction
      );

      this.fire("callInteraction", this);
    }

    if (prevProps.enableCallView !== this.props.enableCallView) {
      this.fire("enableCallView", this);
    }

    if (prevProps.client !== this.props.client) {
      this.fire("client", this);
    }

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Row({
      mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
      children: [
        TextView({
          data: this.title,
          style: new ui.TextStyle({
            fontSize: cStyle.tTextViewHeadlineFourFontSizeOn,
            fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
          }),
          className: "x13c",
          key: "0",
        }),
        ui.Container({ className: "xc8f hc h", key: "1" }),
        this.enableCallView
          ? ui.Container({
              margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
              child: CallingProgressView({
                callInteraction: this.callInteraction,
                client: this.client,
                onCall: () => {
                  this.onCallingInteractionHanlde();
                },
              }),
              className: "x2ca",
            })
          : [],
        this.data.connecting
          ? Loader({ backgroundColor: cStyle.c14, valueColor: cStyle.c1 })
          : ui.Container({
              margin: ui.EdgeInsets.fromLTRB(0.0, 0.0, 10.0, 0.0, new Map()),
              child: TextWithToggle({
                value: this.data.deviceReady,
                name:
                  this.data !== null && this.data.deviceReady
                    ? "Online"
                    : "Offline",
                onChanged: (val) => {
                  this.connectButtonPressed(val);
                },
              }),
              className: "x4a1b",
            }),
      ],
      className: ui.join("PageHeaderView xdc7 hc h ", this.props.className),
      ...copyBaseUIProps(this.props),
    });
  }
  public connectButtonPressed = (val: boolean): void => {
    this.onConnect();
  };
  public onCallingInteractionHanlde = (): void => {
    this.onMaximizeCall();
  };
  public get onConnect(): _PageHeaderViewOnConnect {
    return this.props.onConnect;
  }
  public get onMaximizeCall(): _PageHeaderViewOnMaximizeCall {
    return this.props.onMaximizeCall;
  }
  public dispose(): void {
    this.editProfilePopupPopup?.dispose();

    super.dispose();
  }
  public showEditProfilePopup(
    d3eParams?: Partial<{
      autoClose: boolean;
      model: boolean;
      float: boolean;
      takeFocus: boolean;
    }>
  ): void {
    let autoClose = d3eParams?.autoClose;

    let model = d3eParams?.model;

    let float = d3eParams?.float;

    let takeFocus = d3eParams?.takeFocus;

    this.editProfilePopupPopup?.dispose();

    this.editProfilePopupPopup = new Popup({
      autoClose: autoClose,
      model: model,
      float: float,
      takeFocus: takeFocus,
      position: ui.PopUpPosition.Center,
      child: ui.Container({
        width: 500,
        child: EditProfileView({ user: this.user }),
        className: "xee5 hc vc",
      }),
    });

    this.editProfilePopupPopup.showPopup(this.context);
  }
  public hideEditProfilePopup(): void {
    this.editProfilePopupPopup?.dispose();
  }
}
export default function PageHeaderView(props: PageHeaderViewProps) {
  return React.createElement(_PageHeaderViewState, {
    ..._PageHeaderViewState.defaultProps,
    ...props,
  });
}
